import React, { useState, useRef, useEffect } from 'react';
import { STATUSES, MANUALLY_SELECTABLE_STATUSES, ZONE_SPEC, ZONE_QUOTE, ZONE_COMMITTED, ZONES, fmtInput, statusColor, isABD, assemblyNet, assemblyComponents, isItemReadyForFinalInvoice, filterItems, salesTaxItemStatus, salesTaxShortDate } from '../utils';
import { useDetailJump } from '../lib/jump';
import { Btn, SavedBadge, BackupStatus, StatusDot } from './ui';
import ItemForm, { AssemblyCreateModal, AssemblyDeleteModal } from './ItemForm';
import { ItemSheetButton, PresentationSpecButton, generateItemSheetPdf, generatePresentationSpec } from './documentTabs';
import { clearDraft } from './helpers';
import { useAppContext } from './AppContext';
import QuickCreateModal from './QuickCreateModal';

const SCHEDULE_NAMES = ["Furniture","Lighting","Plumbing / Fixtures","Art & Objects","Hardware","Equipment","Appliances","Tile & Stone","Finish Schedule","Window Treatments","Cushions & Throw Pillows","Softgoods","Millwork"];

const ASSEMBLY_COMPONENTS = {
  'Window Treatments':        ['Fabric','Trim','Workroom','Hardware','Roller Shades'],
  'Cushions & Throw Pillows': ['Fabric','Trim','Workroom'],
  'Lighting':                 ['Bulb'],
  'Plumbing':                 ['Fixture','Trim'],
  'Millwork':                 ['Hardware'],
};

// ZONE_SPEC / ZONE_QUOTE / ZONE_COMMITTED imported from utils.js — single
// source of truth derived from STATUS_ZONE.
function itemZone(status){if(ZONE_COMMITTED.indexOf(status)>=0)return 2;if(ZONE_QUOTE.indexOf(status)>=0)return 1;return 0;}
var DOC_IMAGE_TYPES=new Set(['Spec Sheet','Quote','Purchase Order','Drawing','Other']);
function getDisplayImage(it){
  var imgs=it.images||[];
  if(!imgs.length) return it.imageUrl||'';
  function usable(img){return img&&img.url&&!DOC_IMAGE_TYPES.has(img.type)&&!img.url.toLowerCase().endsWith('.pdf');}
  var hero=imgs[it.heroIndex||0];
  if(usable(hero)) return hero.url;
  var fallback=imgs.find(usable);
  return (fallback&&fallback.url)||it.imageUrl||'';
}

export default function ItemDatabase(p){
  var _ctx=useAppContext();
  var selectionMode=_ctx.selectionMode;
  var selectedItemIds=_ctx.selectedItemIds;
  var toggleSelectionMode=_ctx.toggleSelectionMode;
  var toggleItemSelected=_ctx.toggleItemSelected;
  var clearSelection=_ctx.clearSelection;
  var selectAllVisible=_ctx.selectAllVisible;

  // Allocation-aware "BILL READY" badge: isItemReadyForFinalInvoice
  // reads payment_allocations against the matched deposit invoice
  // (since 2026-05-25). For display purposes here we forward whatever
  // ctx has — if not loaded yet, the badge defers to the next render.
  var _allInvoicesForBadge      = _ctx.invoices || [];
  var _paymentAllocsForBadge    = _ctx.paymentAllocations || [];
  // Payment-aware since 2026-07-05: principal vendor money on the item's
  // PO (deposit included) counts as vendor commitment — see
  // isItemReadyForFinalInvoice in utils.
  var _posForBadge              = _ctx.purchaseOrders || [];
  var _vendorPaymentsForBadge   = _ctx.vendorPayments || [];
  var ms=useState("browse");var mode=ms[0];var setMode=ms[1];
  var eds=useState(null);var editing=eds[0];var setEditing=eds[1];
  var nasm=useState(false);var showNewAsm=nasm[0];var setShowNewAsm=nasm[1];
  var nasmInitSched=useState('');var newAsmInitSched=nasmInitSched[0];var setNewAsmInitSched=nasmInitSched[1];
  var easm=useState(null);var editingAsm=easm[0];var setEditingAsm=easm[1];
  var dasm=useState(null);var deletingAsm=dasm[0];var setDeletingAsm=dasm[1];
  var sfs=useState("All");var schedFilter=sfs[0];var setSchedFilter=sfs[1];
  var pfs=useState("");var projFilter=pfs[0];var setProjFilter=pfs[1];
  var stfs=useState("All");var statusFilter=stfs[0];var setStatusFilter=stfs[1];
  var srs=useState("");var search=srs[0];var setSearch=srs[1];
  var rfs=useState("");var roomFilter=rfs[0];var setRoomFilter=rfs[1];
  var sgs=useState(false);var showSuggest=sgs[0];var setShowSuggest=sgs[1];
  var exs=useState({});var expanded=exs[0];var setExpanded=exs[1];
  var dfs=useState("all");var dateField=dfs[0];var setDateField=dfs[1];
  var dqs=useState("");var dateQuick=dqs[0];var setDateQuick=dqs[1];
  var dfrs=useState({from:"",to:""});var dateRange=dfrs[0];var setDateRange=dfrs[1];
  var gbs=useState("room");var groupBy=gbs[0];var setGroupBy=gbs[1];
  // Sales tax filter (2026-10-15): 'all' | 'collected' (tax collected, not remitted) | 'remitted'.
  var txf=useState('all');var taxFilter=txf[0];var setTaxFilter=txf[1];

  var qcs = useState(false);
  var showQuickCreate = qcs[0];
  var setShowQuickCreate = qcs[1];

  var sdfs=useState(false);var showDateFilter=sdfs[0];var setShowDateFilter=sdfs[1];

  // Honor detail jumps from other tabs — open the matching item's edit form.
  useDetailJump('items', function(id) {
    var found = (p.items || []).find(function(it) { return it.id === id; });
    if (found) { setEditing(found); setMode('edit'); }
  });

  var restoredIdRef=useRef(null);
  useEffect(function(){
    if(p.initialItemId && restoredIdRef.current!==p.initialItemId && p.items && p.items.length>0){
      var found=(p.items||[]).find(function(it){return it.id===p.initialItemId;});
      if(found){
        restoredIdRef.current=p.initialItemId;
        setEditing(found);
        setMode('edit');
      }
    }
  },[p.initialItemId, p.items]);

  useEffect(function(){
    function handlePop(){
      if(mode==="edit"||mode==="new"){
        if(window._hasUnsavedChanges&&!window.confirm("You have unsaved changes. Leave without saving?")){
          if(p.onNavigate)p.onNavigate('/items');
          return;
        }
        clearDraft("item");
        window._hasUnsavedChanges=false;
        setMode("browse");
        setEditing(null);
      }
    }
    window.addEventListener("popstate",handlePop);
    return function(){window.removeEventListener("popstate",handlePop);};
  },[mode]);

  useEffect(function(){
    window._resetItemDatabase=function(){
      if(mode==="edit"||mode==="new"){
        clearDraft("item");
        window._hasUnsavedChanges=false;
        setMode("browse");
        setEditing(null);
      }
    };
    return function(){window._resetItemDatabase=null;};
  },[mode]);

  var parents=(p.items||[]).filter(function(it){return!it.parentId;});
  function childrenOf(id){return(p.items||[]).filter(function(it){return it.parentId===id;});}
  // Sales Tax → "N items on a planned-install date" lands here with
  // #/items?ids=a,b,c (handoff v3 §3): the list narrows to those items so
  // the delivery date can be set on each. Read once on mount; the chip
  // clears it.
  var idf=useState(function(){
    try{var q=String(window.location.hash||'').split('?')[1]||'';var ids=new URLSearchParams(q).get('ids');return ids?ids.split(',').filter(Boolean):[];}catch(e){return [];}
  });var idsFilter=idf[0];var setIdsFilter=idf[1];
  var hasDateFilter=dateQuick!==""||dateRange.from!==""||dateRange.to!=="";
  // filterItems in utils.js is the canonical filter (guarded by
  // itemDatabaseFilter.test.js). This page used to carry an inline copy
  // whose search-field list drifted — SKU/tag/manufacturer were unsearchable.
  var filtered=filterItems(p.items,{
    schedFilter:schedFilter,projFilter:projFilter,roomFilter:roomFilter,
    statusFilter:statusFilter,search:search,hasDateFilter:hasDateFilter,
    dateField:dateField,dateQuick:dateQuick,dateFrom:dateRange.from,dateTo:dateRange.to,
  });
  // Per-item sales tax state (unbilled / collected / remitted) read off the
  // paid FIN + the stored filing stamp — never recomputed from live tax.
  var _taxRefs={invoices:_ctx.invoices||[],paymentAllocations:_ctx.paymentAllocations||[],clientPayments:_ctx.clientPayments||[],salesTaxFilingItems:_ctx.salesTaxFilingItems||[],salesTaxFilings:_ctx.salesTaxFilings||[],salesTaxFilingFiles:_ctx.salesTaxFilingFiles||[],stateTaxRecords:_ctx.stateTaxRecords||[],projects:_ctx.projects||[]};
  var taxStatusById={};
  (p.items||[]).forEach(function(it){taxStatusById[it.id]=salesTaxItemStatus(it,_taxRefs);});
  if(taxFilter!=='all'){
    filtered=filtered.filter(function(it){var st=taxStatusById[it.id];return st&&st.state===taxFilter;});
  }
  if(idsFilter.length){
    filtered=filtered.filter(function(it){return idsFilter.indexOf(it.id)>=0;});
  }
  // The REMITTED column and the muted remitted-row ink show ONLY while the
  // Tax filter is narrowed (Cimon, 2026-10-16: tax information does not
  // belong on the main items page). The default list is tax-free.
  var showTaxCol=taxFilter!=='all';
  function taxRemittedCell(it){
    var st=taxStatusById[it.id];
    if(!st||st.state==='unbilled')return null;
    if(st.state==='collected')return <span style={{color:'#D4007A'}}>not yet · FIN {st.paidBasis==='closed'?'closed':'paid'} {salesTaxShortDate(st.paidDate)} → {st.periodLabel} return</span>;
    var f=st.remittance&&st.remittance.filing;
    return <span style={{color:'#888880'}}><span style={{display:'inline-block',width:7,height:7,borderRadius:'50%',background:'#1A5C38',marginRight:6}} />{[salesTaxShortDate(f&&f.paidDate,true),f&&f.method].filter(Boolean).join(' · ')}</span>;
  }
  function taxNameColor(it){return (showTaxCol&&taxStatusById[it.id]&&taxStatusById[it.id].state==='remitted')?'#888880':'#111111';}
  var groups={};
  if(groupBy==="room"){
    filtered.forEach(function(it){
      var key=it.roomNumber||(it.roomName?"n:"+it.roomName:"");
      if(!key)key="__unassigned__";
      if(!groups[key])groups[key]=[];
      groups[key].push(it);
    });
    Object.keys(groups).forEach(function(k){
      groups[k].sort(function(a,b){var sa=a.schedule||"",sb=b.schedule||"";if(sa!==sb)return sa.localeCompare(sb);return(a.item||"").localeCompare(b.item||"");});
    });
  }else{
    filtered.forEach(function(it){var s=it.schedule||"Unassigned";if(!groups[s])groups[s]=[];groups[s].push(it);});
    Object.keys(groups).forEach(function(s){groups[s].sort(function(a,b){var an=parseFloat(a.roomNumber)||0,bn=parseFloat(b.roomNumber)||0;return an!==bn?an-bn:(a.roomName||'').localeCompare(b.roomName||'');});});
  }
  var groupKeys=groupBy==="room"
    ?Object.keys(groups).sort(function(a,b){if(a==="__unassigned__")return 1;if(b==="__unassigned__")return-1;var na=parseFloat(a),nb=parseFloat(b);if(!isNaN(na)&&!isNaN(nb))return na-nb;if(!isNaN(na))return-1;if(!isNaN(nb))return 1;return String(a).localeCompare(String(b));})
    :Object.keys(groups).sort();
  var tsls=useState(false);var tearSheetLoading=tsls[0];var setTearSheetLoading=tsls[1];
  var ems=useState('');var exportedMsg=ems[0];var setExportedMsg=ems[1];

  function buildDocFilename(item, docType) {
    var tag = item.tag ? item.tag.replace(/[^a-zA-Z0-9\-\/]/g,'') : '';
    var productSlug = (item.productName || item.item || 'Item')
      .replace(/[^a-zA-Z0-9]/g,'').slice(0,30);
    if (docType === 'client-tearsheet' ||
        docType === 'internal-tearsheet') {
      var modeLabel = docType === 'client-tearsheet'
        ? 'Client' : 'Internal';
      var roomSlug = '';
      if (item && item.roomNumber) {
        roomSlug = item.roomNumber
          + (item.roomName
            ? '-' + item.roomName.replace(/[^a-zA-Z0-9]/g,'')
            : '');
      }
      var prefix = roomSlug || productSlug;
      return prefix + '_' + productSlug + '_' + modeLabel + '.pdf';
    }
    return (tag||productSlug) + '_' + docType + '.pdf';
  }

  async function downloadTearSheets(itemIds, mode) {
    var ids = Array.from(itemIds);
    var itemsToSheet = (p.items||[]).filter(function(it){
      return ids.indexOf(it.id) >= 0 && !it.parentId;
    });
    if (!itemsToSheet.length) return;
    setTearSheetLoading(true);
    var failed = [];
    for (var i = 0; i < itemsToSheet.length; i++) {
      var it = itemsToSheet[i];
      try {
        if (mode === 'client') {
          await generatePresentationSpec(
            it,
            it.heroIndex || 0,
            true,   // show client pricing
            []
          );
        } else {
          await generateItemSheetPdf(
            it,
            it.heroIndex || 0,
            '',
            p.vendors || [],
            [],
            false
          );
        }
      } catch(e) {
        console.warn('Tear sheet failed for', it.item, e);
        failed.push(it.productName || it.item || 'Unknown');
      }
    }
    setTearSheetLoading(false);
    if (failed.length > 0) {
      alert('Could not generate: ' + failed.join(', '));
    }
  }

  var allVisibleIds=[];
  filtered.forEach(function(it){
    allVisibleIds.push(it.id);
    childrenOf(it.id).forEach(function(c){allVisibleIds.push(c.id);});
  });
  var allVisibleSelected=allVisibleIds.length>0&&allVisibleIds.every(function(id){return selectedItemIds.has(id);});

  function toggleGroup(s){setExpanded(function(prev){var n=Object.assign({},prev);n[s]=!prev[s];return n;});}
  function handleSave(item){
    var kids=item.children||[];var parent=Object.assign({},item);
    parent.children=kids.map(function(c){return{childType:c.childType||'',item:c.item||'',vendor:c.vendor||'',specs:c.specs||{},importSource:c.importSource||''};});
    var childItems=kids.map(function(c){var existing=(p.items||[]).find(function(it){return it.id===c.id;})||{};return Object.assign({},existing,c,{parentId:parent.id,project:parent.project,roomName:parent.roomName,roomNumber:parent.roomNumber,sidemark:parent.sidemark,status:parent.status,date:parent.date});});
    var existing=(p.items||[]).find(function(it){return it.id===parent.id;});
    if(existing){p.onUpdate(parent.id,parent);}else{p.onAdd(parent,childItems);}
    if(p.onSaveDirect){
      p.onSaveDirect(parent);
      childItems.forEach(function(c){if(c&&c.id)p.onSaveDirect(c);});
    }
    clearDraft("item");
    window._hasUnsavedChanges=false;
    setEditing(Object.assign({},parent,{children:kids}));
    setMode("edit");
  }

  function openNewItemForAssembly(asm,componentType){
    clearDraft("item");
    window._hasUnsavedChanges=false;
    var roomName=asm.room_name||'';
    var roomNumber=asm.room_number||'';
    if(!roomName||!roomNumber){
      var sib=(p.items||[]).find(function(it){return it.assemblyId===asm.id&&(it.roomName||it.roomNumber);});
      if(sib){roomName=sib.roomName||roomName;roomNumber=sib.roomNumber||roomNumber;}
    }
    setEditing({_formKey:Date.now(),assemblyId:asm.id,schedule:asm.schedule||'',project:asm.project_label||'',roomName:roomName,roomNumber:roomNumber,sidemark:asm.sidemark||'',childType:componentType||'',tag:'',status:'Specifying'});
    setMode("new");
    if(p.onNavigate)p.onNavigate('/items');
  }

  function activeIds(){return Array.from(selectedItemIds);}
  function clearActive(){clearSelection();}

  function bulkDelete(){
    var ids=activeIds();
    if(!window.confirm("Delete "+ids.length+" selected item"+(ids.length!==1?"s":"")+"? This cannot be undone."))return;
    ids.forEach(function(id){p.onDelete(id);});
    clearActive();
  }
  function bulkReassignProject(proj){
    if(!proj)return;
    activeIds().forEach(function(id){
      var item=(p.items||[]).find(function(it){return it.id===id;});
      if(item)p.onUpdate(id,Object.assign({},item,{project:proj,projectLabel:proj}));
    });
    clearActive();
  }
  function bulkReassignStatus(status){
    if(!status)return;
    activeIds().forEach(function(id){
      var item=(p.items||[]).find(function(it){return it.id===id;});
      if(item)p.onUpdate(id,Object.assign({},item,{status:status}));
    });
    clearActive();
  }
  async function handleDeleteAssemblyAll(asm,memberItems){
    if(!p.onDeleteAssembly)return;
    var delFn=p.onDeleteDirect||p.onDelete;
    for(var i=0;i<memberItems.length;i++){if(delFn)await delFn(memberItems[i].id);}
    await p.onDeleteAssembly(asm.id);
    setDeletingAsm(null);
  }
  async function handleDeleteAssemblyOnly(asm,memberItems){
    if(!p.onDeleteAssembly)return;
    for(var i=0;i<memberItems.length;i++){if(p.onUpdate)await p.onUpdate(memberItems[i].id,{assemblyId:null,assembly_id:null});}
    await p.onDeleteAssembly(asm.id);
    setDeletingAsm(null);
  }

  if(mode==="new"||mode==="edit"){return (
    <div>
      {showNewAsm&&<AssemblyCreateModal onClose={function(){setShowNewAsm(false);}} projects={p.projects} initialSchedule={newAsmInitSched} onSave={p.onAddAssembly} />}
      {editingAsm&&<AssemblyCreateModal editAssembly={editingAsm} onClose={function(){setEditingAsm(null);}} projects={p.projects} onSave={function(patch){return p.onUpdateAssembly(editingAsm.id,patch);}} />}
      {deletingAsm&&<AssemblyDeleteModal assembly={deletingAsm.asm} memberCount={deletingAsm.members.length} onDeleteAll={function(){handleDeleteAssemblyAll(deletingAsm.asm,deletingAsm.members);}} onDeleteAssemblyOnly={function(){handleDeleteAssemblyOnly(deletingAsm.asm,deletingAsm.members);}} onClose={function(){setDeletingAsm(null);}} />}
<ItemForm key={editing?(editing.id||editing._formKey||"new"):"new"} initial={editing} vendors={p.vendors} projects={p.projects} allItems={parents} assemblies={p.assemblies||[]} isOwner={p.isOwner} onAddVendor={p.onAddVendor} onSave={handleSave} onCancel={function(){clearDraft("item");window._hasUnsavedChanges=false;setMode("browse");setEditing(null);if(p.onNavigate)p.onNavigate('/items');}} onUpdate={p.onUpdate} onAddAssembly={p.onAddAssembly} onAddSiblingComponent={function(assemblyId){var asm=(p.assemblies||[]).find(function(a){return a.id===assemblyId;});if(asm)openNewItemForAssembly(asm,'');}} onSaveDirect={p.onSaveDirect} onGenerateWorkroomSheet={generateItemSheetPdf} onGenerateClientSheet={generatePresentationSpec} proposals={p.proposals||[]} />
    </div>
  );}

  var bulkBar = (selectionMode && selectedItemIds.size > 0) ? (
    <div style={{display:'flex',alignItems:'center',
      height:44,padding:'0 14px',gap:6,
      background:'#FFFFFF',
      borderBottom:'0.5px solid #D8D7D2',
      borderTop:'1.5px solid #111111'}}>

      <div style={{display:'inline-flex',alignItems:'center',
        gap:6,height:26,padding:'0 10px',borderRadius:13,
        border:'1.5px solid #111111',flexShrink:0}}>
        <span style={{fontSize:14,fontWeight:500,
          lineHeight:1,color:'#111111'}}>
          {selectedItemIds.size}
        </span>
        <span style={{fontSize:9,fontWeight:500,
          letterSpacing:'0.08em',textTransform:'uppercase',
          color:'#888880',lineHeight:1}}>
          Selected
        </span>
      </div>

      <div style={{width:'0.5px',height:18,
        background:'#D8D7D2',margin:'0 2px',flexShrink:0}} />

      <button
        onClick={function(){
          if(allVisibleSelected) clearSelection();
          else selectAllVisible(allVisibleIds);
        }}
        style={{height:26,padding:'0 10px',borderRadius:4,
          border:'0.5px solid #D8D7D2',background:'#FFFFFF',
          cursor:'pointer',fontSize:10,fontWeight:500,
          color:'#111111',fontFamily:'inherit',
          whiteSpace:'nowrap',flexShrink:0}}>
        {allVisibleSelected?'Deselect all':'Select all'}
      </button>

      <div style={{width:'0.5px',height:18,
        background:'#D8D7D2',margin:'0 2px',flexShrink:0}} />

      <button
        onClick={function(){downloadTearSheets(selectedItemIds,'client');}}
        style={{height:26,padding:'0 10px',borderRadius:4,
          border:'0.5px solid #D8D7D2',background:'#FFFFFF',
          cursor:'pointer',fontSize:10,fontWeight:500,
          color:'#111111',fontFamily:'inherit',
          whiteSpace:'nowrap',flexShrink:0}}>
        Client tear sheet
      </button>

      <button
        onClick={function(){downloadTearSheets(selectedItemIds,'internal');}}
        style={{height:26,padding:'0 10px',borderRadius:4,
          border:'0.5px solid #D8D7D2',background:'#FFFFFF',
          cursor:'pointer',fontSize:10,fontWeight:500,
          color:'#111111',fontFamily:'inherit',
          whiteSpace:'nowrap',flexShrink:0}}>
        Internal sheet
      </button>

      <div style={{width:'0.5px',height:18,
        background:'#D8D7D2',margin:'0 2px',flexShrink:0}} />

      <select
        onChange={function(e){
          bulkReassignProject(e.target.value);
          e.target.value='';
        }}
        style={{height:26,border:'0.5px solid #D8D7D2',
          borderRadius:4,padding:'0 8px',fontSize:10,
          fontFamily:'inherit',background:'#FFFFFF',
          color:'#111111',outline:'none',cursor:'pointer'}}>
        <option value=''>Reassign project…</option>
        {(p.projects||[]).map(function(proj){
          return <option key={proj.id} value={proj.label}>
            {proj.label}
          </option>;
        })}
      </select>

      <select
        data-testid="bulk-status-select"
        onChange={function(e){
          bulkReassignStatus(e.target.value);
          e.target.value='';
        }}
        style={{height:26,border:'0.5px solid #D8D7D2',
          borderRadius:4,padding:'0 8px',fontSize:10,
          fontFamily:'inherit',background:'#FFFFFF',
          color:'#111111',outline:'none',cursor:'pointer'}}>
        <option value=''>Set status…</option>
        {/* Restricted to manually-selectable statuses (Specifying through On
            Proposal). Bulk-reassigning to Client Approved or beyond bypasses
            approveProposal's SKU/peer cascade; bulk-reassigning to Cancelled
            bypasses the credit-ledger entries CancelItemModal writes. */}
        {MANUALLY_SELECTABLE_STATUSES.map(function(s){
          return <option key={s} value={s}>{s}</option>;
        })}
      </select>

      <div style={{width:'0.5px',height:18,
        background:'#D8D7D2',margin:'0 2px',flexShrink:0}} />

      <button
        onClick={bulkDelete}
        style={{height:26,padding:'0 10px',borderRadius:4,
          border:'0.5px solid #F5C0B8',background:'#FFFFFF',
          cursor:'pointer',fontSize:10,fontWeight:500,
          color:'#C8322A',fontFamily:'inherit',
          whiteSpace:'nowrap',flexShrink:0}}>
        Delete
      </button>

      <div style={{flex:1}} />

      <button
        onClick={function(){
          clearSelection();
          toggleSelectionMode();
        }}
        style={{height:26,padding:'0 10px',borderRadius:4,
          border:'0.5px solid #D8D7D2',background:'#FFFFFF',
          cursor:'pointer',fontSize:10,color:'#888880',
          fontFamily:'inherit',whiteSpace:'nowrap',
          flexShrink:0}}>
        Cancel ✕
      </button>

    </div>
  ) : null;

  var summaryTotals={spec:0,quote:0,committed:0};
  filtered.forEach(function(it){if(isABD(it))return;var net=assemblyNet(it,p.items);var z=itemZone(it.status||'');if(z===0)summaryTotals.spec+=net;else if(z===1)summaryTotals.quote+=net;else summaryTotals.committed+=net;});
  var summaryTotal=summaryTotals.spec+summaryTotals.quote+summaryTotals.committed;
  var summaryBar=(
    <div style={{display:"flex",gap:32,alignItems:"center",padding:"12px 20px",background:"#fff",borderTop:"0.5px solid #E0E0DC",fontFamily:"'DM Sans',sans-serif"}}>
      {[{label:"Specifying",amt:summaryTotals.spec,color:"#F5C800"},{label:"Quoting",amt:summaryTotals.quote,color:"#D4007A"},{label:"Committed",amt:summaryTotals.committed,color:"#1A5C38"},{label:"Total",amt:summaryTotal,color:"#111"}].map(function(z){
        return (
          <div key={z.label} style={{display:"flex",flexDirection:"column",gap:2}}>
            <span style={{fontSize:10,fontWeight:500,letterSpacing:"0.07em",textTransform:"uppercase",color:"#bbb"}}>{z.label}</span>
            <span style={{fontSize:13,fontWeight:500,color:z.color}}>{"$"+z.amt.toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0})}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      {showNewAsm&&<AssemblyCreateModal onClose={function(){setShowNewAsm(false);}} projects={p.projects} initialSchedule={newAsmInitSched} onSave={p.onAddAssembly} />}
      {editingAsm&&<AssemblyCreateModal editAssembly={editingAsm} onClose={function(){setEditingAsm(null);}} projects={p.projects} onSave={function(patch){return p.onUpdateAssembly(editingAsm.id,patch);}} />}
      {deletingAsm&&<AssemblyDeleteModal assembly={deletingAsm.asm} memberCount={deletingAsm.members.length} onDeleteAll={function(){handleDeleteAssemblyAll(deletingAsm.asm,deletingAsm.members);}} onDeleteAssemblyOnly={function(){handleDeleteAssemblyOnly(deletingAsm.asm,deletingAsm.members);}} onClose={function(){setDeletingAsm(null);}} />}
      {showQuickCreate && <QuickCreateModal
        projects={p.projects}
        schedules={SCHEDULE_NAMES}
        onClose={function(){ setShowQuickCreate(false); }}
        onSubmit={function(formData){
          clearDraft('item');
          window._hasUnsavedChanges = false;
          setEditing(Object.assign({}, formData, { status: 'Specifying' }));
          setMode('new');
          setShowQuickCreate(false);
          if(p.onNavigate) p.onNavigate('/items');
        }}
      />}
      <div style={{display:'flex',alignItems:'center',height:44,
        padding:'0 14px',gap:6,
        borderBottom:'0.5px solid #D8D7D2',
        background:'#F7F7F5'}}>

        <button
          onClick={function(){
            setShowQuickCreate(true);
          }}
          style={{height:28,padding:'0 11px',borderRadius:5,
            background:'#111111',color:'#FFFFFF',
            border:'0.5px solid #111111',cursor:'pointer',
            fontSize:11,fontWeight:500,fontFamily:'inherit',
            flexShrink:0}}>
          + New item
        </button>

        <button
          onClick={function(){setNewAsmInitSched('');setShowNewAsm(true);}}
          style={{height:28,padding:'0 11px',borderRadius:5,
            background:'#FFFFFF',color:'#111111',
            border:'0.5px solid #D8D7D2',cursor:'pointer',
            fontSize:11,fontWeight:500,fontFamily:'inherit',
            flexShrink:0}}>
          + Assembly
        </button>

        <div style={{width:'0.5px',height:18,background:'#D8D7D2',
          margin:'0 2px',flexShrink:0}} />

        <div style={{display:'flex',height:28,
          border:'0.5px solid #D8D7D2',borderRadius:5,
          overflow:'hidden',flexShrink:0}}>
          <button
            onClick={function(){setGroupBy('room');}}
            style={{height:'100%',padding:'0 11px',border:'none',
              borderRight:'0.5px solid #D8D7D2',
              background:groupBy==='room'?'#111111':'#FFFFFF',
              color:groupBy==='room'?'#FFFFFF':'#888880',
              cursor:'pointer',fontSize:11,fontWeight:500,
              fontFamily:'inherit',whiteSpace:'nowrap'}}>
            By Room
          </button>
          <button
            onClick={function(){setGroupBy('schedule');}}
            style={{height:'100%',padding:'0 11px',border:'none',
              background:groupBy==='schedule'?'#111111':'#FFFFFF',
              color:groupBy==='schedule'?'#FFFFFF':'#888880',
              cursor:'pointer',fontSize:11,fontWeight:500,
              fontFamily:'inherit',whiteSpace:'nowrap'}}>
            By Schedule
          </button>
        </div>

        <div style={{width:'0.5px',height:18,background:'#D8D7D2',
          margin:'0 2px',flexShrink:0}} />

        <div style={{position:'relative',flexShrink:0}}>
          <input
            value={search}
            onChange={function(e){setSearch(e.target.value);}}
            onFocus={function(){setShowSuggest(true);}}
            onBlur={function(){
              setTimeout(function(){setShowSuggest(false);},200);
            }}
            placeholder='Search items…' autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
            style={{height:28,width:200,
              border:'0.5px solid #D8D7D2',borderRadius:5,
              padding:'0 '+(search?'28px':'10px')+' 0 10px',
              fontSize:11,fontFamily:'inherit',
              color:'#111111',background:'#FFFFFF',outline:'none'}}
          />
          {search&&(
            <button
              onClick={function(){setSearch('');}}
              style={{position:'absolute',right:8,top:'50%',
                transform:'translateY(-50%)',background:'none',
                border:'none',cursor:'pointer',fontSize:12,
                color:'#888880',padding:0,lineHeight:1}}>
              ✕
            </button>
          )}
          {showSuggest&&search.length>=1&&(function(){
            var _sq=search.toLowerCase();
            var suggestions=parents.filter(function(it){
              return [it.item,it.productName,it.vendor,it.roomName,
                it.sidemark,it.tag,it.itemNumber,it.manufacturer,it.model].some(function(v){
                  return v&&String(v).toLowerCase().indexOf(_sq)>=0;
              });
            }).slice(0,8);
            if(!suggestions.length)return null;
            return (
              <div style={{position:'absolute',top:'100%',left:0,
                zIndex:200,background:'#FFFFFF',
                border:'0.5px solid #D8D7D2',borderRadius:6,
                minWidth:300,maxHeight:280,overflowY:'auto',
                marginTop:2}}>
                {suggestions.map(function(it){
                  return (
                    <div key={it.id}
                      onMouseDown={function(){
                        setSearch('');setShowSuggest(false);
                        setEditing(it);setMode('edit');
                        if(p.onNavigate)p.onNavigate('/items/'+it.id);
                      }}
                      style={{padding:'8px 12px',cursor:'pointer',
                        borderBottom:'0.5px solid #EFEFEC',
                        display:'flex',flexDirection:'column',gap:2}}
                      onMouseOver={function(e){
                        e.currentTarget.style.background='#F7F7F5';
                      }}
                      onMouseOut={function(e){
                        e.currentTarget.style.background='#FFFFFF';
                      }}>
                      <div style={{fontSize:12,fontWeight:500,
                        color:'#111111'}}>
                        {it.item||''}
                      </div>
                      <div style={{fontSize:11,color:'#888880',
                        display:'flex',gap:8}}>
                        {it.vendor&&<span>{it.vendor}</span>}
                        {it.roomName&&<span>· {it.roomName}</span>}
                        {it.tag&&<span style={{color:'#D4007A',
                          fontWeight:500}}>· {it.tag}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        <div style={{flex:1}} />

        <button
          onClick={toggleSelectionMode}
          style={{height:28,padding:'0 11px',borderRadius:5,
            background:selectionMode?'#FFFFFF':'#D4007A',
            color:selectionMode?'#D4007A':'#FFFFFF',
            border:'0.5px solid #D4007A',cursor:'pointer',
            fontSize:11,fontWeight:500,fontFamily:'inherit',
            flexShrink:0}}>
          {selectionMode?'Deselect':'Select'}
        </button>

        <div style={{width:'0.5px',height:18,background:'#D8D7D2',
          margin:'0 2px',flexShrink:0}} />

        <button
          onClick={function(){
            var hd=['Item','Schedule','Project','Room #','Room','Vendor','Qty','Status','Net Price'];
            var rows=filtered.map(function(it){
              return [it.item||'',it.schedule||'',it.project||'',
                it.roomNumber||'',it.roomName||'',it.vendor||'',
                (it.qty||'')+(it.qtyType?' '+it.qtyType:''),
                it.status||'',it.netPrice||''];
            });
            var csv=[hd].concat(rows).map(function(r){
              return r.map(function(c){return '"'+String(c||'').replace(/"/g,'""')+'"';}).join(',');
            }).join('\n');
            var a=document.createElement('a');
            a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
            a.download='items-export-all-'+new Date().toISOString().slice(0,10)+'.csv';
            a.click();
            var count=filtered.length;
            setExportedMsg(count+' item'+(count!==1?'s':''));
            setTimeout(function(){setExportedMsg('');},2000);
          }}
          style={{height:28,padding:'0 11px',borderRadius:5,
            background:'#FFFFFF',color:'#111111',
            border:'0.5px solid #D8D7D2',cursor:'pointer',
            fontSize:11,fontWeight:500,fontFamily:'inherit',
            flexShrink:0}}>
          Export CSV
        </button>

        {exportedMsg&&(
          <span style={{fontSize:11,color:'#1A5C38',fontFamily:'inherit'}}>
            ✓ {exportedMsg} exported
          </span>
        )}

        <SavedBadge on={p.saved} />
        <BackupStatus />

      </div>
      <div style={{display:'flex',alignItems:'center',height:36,
        padding:'0 14px',gap:6,overflowX:'auto',
        borderBottom:'0.5px solid #D8D7D2',
        background:'#FFFFFF',flexShrink:0}}>

        {(function(){
          var activeSchedules=SCHEDULE_NAMES.filter(function(s){
            return (p.items||[]).some(function(it){return it.schedule===s&&!it.parentId;});
          });
          return ['All'].concat(activeSchedules).map(function(s){
            var on=schedFilter===s;
            return (
              <button key={s} onClick={function(){setSchedFilter(s);}}
                style={{height:24,padding:'0 10px',borderRadius:12,
                  border:on?'1.5px solid #111111':'0.5px solid #D8D7D2',
                  background:on?'#111111':'#FFFFFF',
                  color:on?'#FFFFFF':'#888880',
                  fontSize:10,fontWeight:500,fontFamily:'inherit',
                  cursor:'pointer',whiteSpace:'nowrap',flexShrink:0}}>
                {s}
              </button>
            );
          });
        })()}

        <div style={{width:2,height:16,background:'#D4007A',
          borderRadius:1,margin:'0 8px',flexShrink:0}} />

        {(function(){
          var projs=[];
          (p.items||[]).forEach(function(it){
            if(it.project&&projs.indexOf(it.project)<0)projs.push(it.project);
          });
          projs.sort();
          return projs.map(function(proj){
            var on=projFilter===proj;
            return (
              <button key={proj} onClick={function(){setProjFilter(on?'':proj);}}
                style={{height:24,padding:'0 10px',borderRadius:12,
                  border:on?'1.5px solid #111111':'0.5px solid #D8D7D2',
                  background:on?'#111111':'#FFFFFF',
                  color:on?'#FFFFFF':'#888880',
                  fontSize:10,fontWeight:500,fontFamily:'inherit',
                  cursor:'pointer',whiteSpace:'nowrap',flexShrink:0}}>
                {proj}
              </button>
            );
          });
        })()}

        <div style={{width:2,height:16,background:'#D4007A',
          borderRadius:1,margin:'0 8px',flexShrink:0}} />

        {idsFilter.length>0&&(
          <button onClick={function(){setIdsFilter([]);try{var h=String(window.location.hash||'').split('?')[0];window.history.replaceState(null,'',h);}catch(e){}}} data-testid="ids-filter-chip"
            title="Narrowed to the items the Sales Tax page sent you to. Click to show every item."
            style={{height:26,padding:'0 12px',borderRadius:13,border:'1.5px solid #111111',background:'#FFFFFF',color:'#111111',fontSize:11,fontWeight:500,fontFamily:'inherit',cursor:'pointer',whiteSpace:'nowrap',flexShrink:0}}>
            {idsFilter.length} item{idsFilter.length===1?'':'s'} from Sales Tax · ×
          </button>
        )}
        {[['all','Tax: all'],['collected','Tax collected, not remitted'],['remitted','Remitted']].map(function(opt){
          var on=taxFilter===opt[0];
          return (
            <button key={opt[0]} onClick={function(){setTaxFilter(opt[0]);}} data-testid={'tax-filter-'+opt[0]}
              style={{height:26,padding:'0 12px',borderRadius:13,
                border:on?'1.5px solid #111111':'0.5px solid #D8D7D2',
                background:'#FFFFFF',color:on?'#111111':'#888880',
                fontSize:11,fontWeight:500,fontFamily:'inherit',
                cursor:'pointer',whiteSpace:'nowrap',flexShrink:0}}>
              {opt[1]}
            </button>
          );
        })}

        <div style={{flex:1}} />

        <button onClick={function(){
            if(hasDateFilter){setDateQuick('');setDateRange({from:'',to:''});setDateField('all');setShowDateFilter(false);}
            else{setShowDateFilter(function(v){return !v;});}
          }}
          style={{height:24,padding:'0 10px',borderRadius:12,
            border:(hasDateFilter||showDateFilter)?'1.5px solid #111111':'0.5px solid #D8D7D2',
            background:(hasDateFilter||showDateFilter)?'#111111':'#FFFFFF',
            color:(hasDateFilter||showDateFilter)?'#FFFFFF':'#888880',
            fontSize:10,fontWeight:500,fontFamily:'inherit',
            cursor:'pointer',whiteSpace:'nowrap',flexShrink:0}}>
          {hasDateFilter?'Date ✕':'Date filter'}
        </button>
      </div>

      {showDateFilter&&(
        <div style={{display:'flex',gap:8,alignItems:'center',
          padding:'8px 14px',borderBottom:'0.5px solid #D8D7D2',
          background:'#F7F7F5',flexWrap:'wrap'}}>
          <select value={dateField}
            onChange={function(e){setDateField(e.target.value);setDateQuick('');setDateRange({from:'',to:''}); }}
            style={{padding:'4px 8px',border:'0.5px solid #D8D7D2',borderRadius:5,
              fontSize:11,background:'#fff',fontFamily:'inherit',color:'#111111'}}>
            <option value="all">Any date</option>
            <option value="created">Date entered</option>
            <option value="ordered">Date ordered</option>
            <option value="received">Date received</option>
            <option value="status">Last status change</option>
          </select>
          {dateField!=='all'&&(
            <div style={{display:'flex',gap:4}}>
              {['week','month','quarter',''].map(function(q){
                return (
                  <button key={q||'custom'}
                    onClick={function(){setDateQuick(q);if(q)setDateRange({from:'',to:''}); }}
                    style={{padding:'3px 9px',borderRadius:12,
                      border:'0.5px solid #D8D7D2',fontSize:10,fontWeight:500,
                      cursor:'pointer',fontFamily:'inherit',
                      background:dateQuick===q&&(q||(!dateRange.from&&!dateRange.to))?'#111111':'#FFFFFF',
                      color:dateQuick===q&&(q||(!dateRange.from&&!dateRange.to))?'#FFFFFF':'#888880'}}>
                    {q==='week'?'Last 7d':q==='month'?'Last 30d':q==='quarter'?'Last 90d':'Custom'}
                  </button>
                );
              })}
            </div>
          )}
          {dateField!=='all'&&(dateQuick===''||dateRange.from||dateRange.to)&&(
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              <input type="date" value={dateRange.from}
                onChange={function(e){setDateRange(function(prev){return Object.assign({},prev,{from:e.target.value});});setDateQuick('');}}
                style={{padding:'3px 7px',border:'0.5px solid #D8D7D2',borderRadius:5,fontSize:11,fontFamily:'inherit'}} />
              <span style={{fontSize:10,color:'#888880'}}>to</span>
              <input type="date" value={dateRange.to}
                onChange={function(e){setDateRange(function(prev){return Object.assign({},prev,{to:e.target.value});});setDateQuick('');}}
                style={{padding:'3px 7px',border:'0.5px solid #D8D7D2',borderRadius:5,fontSize:11,fontFamily:'inherit'}} />
            </div>
          )}
          {hasDateFilter&&(
            <button onClick={function(){setDateQuick('');setDateRange({from:'',to:''});setDateField('all');setShowDateFilter(false);}}
              style={{padding:'3px 9px',fontSize:10,color:'#888880',
                border:'none',background:'none',cursor:'pointer',fontFamily:'inherit'}}>
              ✕ Clear
            </button>
          )}
        </div>
      )}
      {bulkBar}
      {filtered.length===0
        ? <div style={{textAlign:"center",padding:40,color:"#bbb",fontSize:13}}>No items yet. Click + New Item to get started.</div>
        : <div>
            {groupKeys.map(function(sched){
              var groupItems=groups[sched];var isExp=expanded[sched]!==false;
              var groupLabel=(function(){
                if(groupBy!=="room")return sched;
                if(sched==="__unassigned__")return"Unassigned";
                if(sched.indexOf("n:")===0)return sched.slice(2);
                var fi=groupItems[0];var rn=fi?fi.roomName:"";
                return rn?sched+"—"+rn:sched;
              })();
              var tbody=[];
              var asmMap={};var standalone=[];
              groupItems.forEach(function(it){
                var key=it.assemblyId?('id:'+it.assemblyId):(it.assemblyName?('nm:'+it.assemblyName):null);
                if(key){if(!asmMap[key])asmMap[key]=[];asmMap[key].push(it);}
                else{standalone.push(it);}
              });

              function renderItemRow(it){
                var isChecked=selectedItemIds.has(it.id);
                var rowSelected=isChecked;
                var rowBg=isChecked?"#FFF9E6":"#FFFFFF";
                var imgSrc=getDisplayImage(it);
                return (
                  <tr key={it.id}
                    style={{borderBottom:"0.5px solid #EFEFEC",background:rowBg}}
                    onClick={function(){if(selectionMode)toggleItemSelected(it.id);}}
                    onMouseEnter={function(e){if(!rowSelected)e.currentTarget.style.background="#FAFAFA";}}
                    onMouseLeave={function(e){if(!rowSelected)e.currentTarget.style.background=rowBg;}}>
                    <td style={{padding:'0 8px',width:36,textAlign:'center',verticalAlign:'middle'}}>
                      {selectionMode&&(
                        rowSelected
                          ?<div onClick={function(e){e.stopPropagation();toggleItemSelected(it.id);}}
                             style={{width:15,height:15,borderRadius:3,border:'1.5px solid #111111',background:'#111111',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto'}}>
                             <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                           </div>
                          :<div onClick={function(e){e.stopPropagation();toggleItemSelected(it.id);}}
                             style={{width:15,height:15,borderRadius:3,border:'1.5px solid #D8D7D2',background:'#FFFFFF',cursor:'pointer',margin:'0 auto'}}>
                           </div>
                      )}
                    </td>
                    <td style={{padding:'8px 10px',width:110,verticalAlign:'middle'}}>
                      <span style={{fontSize:9,color:it.itemNumber?'#555552':'#D8D7D2',fontWeight:400,whiteSpace:'nowrap'}}>
                        {it.itemNumber||'—'}
                      </span>
                    </td>
                    <td style={{padding:'4px 8px',width:44,verticalAlign:'middle'}}>
                      {imgSrc
                        ?<img src={imgSrc} alt="" onError={function(e){e.target.style.display="none";}} style={{width:36,height:36,objectFit:"cover",borderRadius:4,border:"0.5px solid #D8D7D2",display:'block'}} />
                        :<div style={{width:36,height:36,borderRadius:4,border:"0.5px dashed #D8D7D2",background:"#F7F7F5"}}></div>
                      }
                    </td>
                    <td style={{padding:'8px 10px',cursor:selectionMode?'default':'pointer'}}
                      onClick={function(){if(!selectionMode){setEditing(it);setMode("edit");if(p.onNavigate)p.onNavigate('/items/'+it.id);}}}>
                      <div style={{display:'flex',alignItems:'center',gap:5}}>
                        <StatusDot status={it.status} />
                        {it.childType&&<span style={{fontSize:9,color:'#888880',fontWeight:500,marginRight:2}}>{it.childType}</span>}
                        <span style={{fontSize:12,fontWeight:500,color:taxNameColor(it),overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:280}}>
                          {it.productName||(it.item||'')}
                        </span>
                        {isABD(it)&&<span style={{fontSize:9,fontWeight:500,padding:'1px 5px',borderRadius:3,border:'0.5px solid #E0E0DC',background:'#fff',color:'#888880',marginLeft:2,whiteSpace:'nowrap'}}>ABD</span>}
                        {it.procurementClass==='owner_furnished'&&<span data-testid="procurement-class-badge" data-class="owner_furnished" title="Owner Furnished — client supplies the item; excluded from billing" style={{fontSize:9,fontWeight:500,padding:'1px 5px',borderRadius:3,border:'0.5px solid #E0E0DC',background:'#fff',color:'#888880',marginLeft:2,whiteSpace:'nowrap'}}>OF</span>}
                        {it.procurementClass==='by_others'&&<span data-testid="procurement-class-badge" data-class="by_others" title="By Others — installed by another trade; excluded from billing" style={{fontSize:9,fontWeight:500,padding:'1px 5px',borderRadius:3,border:'0.5px solid #E0E0DC',background:'#fff',color:'#888880',marginLeft:2,whiteSpace:'nowrap'}}>BO</span>}
                        {isItemReadyForFinalInvoice(it, _allInvoicesForBadge, _paymentAllocsForBadge, p.allItems, _posForBadge, _vendorPaymentsForBadge)&&!it.finalInvoiceId&&<span title="Ready to bundle into a final invoice — Client Invoices → Orders in Process" style={{fontSize:9,fontWeight:500,padding:'1px 5px',borderRadius:3,background:'#EAF3EE',color:'#1A5C38',marginLeft:2,whiteSpace:'nowrap'}}>BILL READY</span>}
                      </div>
                    </td>
                    <td style={{padding:'8px 10px',fontSize:11,color:'#555552',width:120}}>{it.project||'—'}</td>
                    <td style={{padding:'8px 10px',fontSize:11,color:'#555552',width:100}}>{it.roomName||'—'}</td>
                    <td style={{padding:'8px 10px',fontSize:11,color:'#555552',width:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{it.vendor||'—'}</td>
                    <td style={{padding:'8px 10px',width:100}}>
                      {it.netPrice?<div style={{fontSize:11,fontWeight:500,color:'#111111'}}>{fmtInput(it.netPrice)}</div>:<div style={{fontSize:11,color:'#D8D7D2'}}>—</div>}
                      <div style={{fontSize:10,color:'#888880',marginTop:1}}>{(it.qty||'')+(it.qtyType?' '+it.qtyType:'')}</div>
                    </td>
                    <td style={{padding:'8px 10px',fontSize:11,color:'#555552',width:100}}>{it.status||'—'}</td>
                    {showTaxCol&&<td style={{padding:'8px 10px',fontSize:11,whiteSpace:'nowrap'}} data-testid={'tax-remitted-'+it.id}>{taxRemittedCell(it)}</td>}
                  </tr>
                );
              }

              function cmpRoomNum(a,b){var na=parseFloat(a),nb=parseFloat(b);if(!isNaN(na)&&!isNaN(nb))return na-nb;return String(a||'').localeCompare(String(b||''));}
              var renderQueue=[];
              Object.keys(asmMap).forEach(function(asmKey){var fi=asmMap[asmKey][0]||{};renderQueue.push({type:'asm',key:asmKey,roomNum:fi.roomNumber||'',zone:itemZone(fi.status||''),sched:fi.schedule||'',iname:fi.item||''});});
              standalone.forEach(function(it){renderQueue.push({type:'item',item:it,roomNum:it.roomNumber||'',zone:itemZone(it.status||''),sched:it.schedule||'',iname:it.item||''});});
              renderQueue.sort(function(a,b){if(a.zone!==b.zone)return a.zone-b.zone;if(groupBy==="room"){if(a.sched!==b.sched)return a.sched.localeCompare(b.sched);return a.iname.localeCompare(b.iname);}return cmpRoomNum(a.roomNum,b.roomNum);});
              // ZONES imported from utils.js — itemZone() returns an index
              // (0 = spec, 1 = quote, 2 = committed) that maps directly to
              // ZONES[i].label and ZONES[i].color.
              var currentZone=-1;

              renderQueue.forEach(function(entry){
                var zone=entry.zone;
                if(zone!==currentZone){
                  if(currentZone===1&&zone===2){
                    tbody.push(
                      <tr key="lockbar">
                        <td colSpan={showTaxCol?10:9} style={{padding:'5px 20px',background:'#f0f7f4',borderTop:'1.5px solid #1A5C38',borderBottom:'1px solid #c8e6c9'}}>
                          <span style={{fontSize:10,fontWeight:600,letterSpacing:'0.07em',textTransform:'uppercase',color:'#1A5C38'}}>🔒  Client Approved — fields locked</span>
                        </td>
                      </tr>
                    );
                  }
                  tbody.push(
                    <tr key={"zone-"+zone}>
                      <td colSpan={showTaxCol?10:9} style={{padding:'12px 20px 4px',background:'#fff'}}>
                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                          <div style={{width:6,height:6,borderRadius:'50%',background:ZONES[zone].color,flexShrink:0}}></div>
                          <span style={{fontSize:10,fontWeight:500,letterSpacing:'0.07em',textTransform:'uppercase',color:'#999'}}>{ZONES[zone].label}</span>
                        </div>
                      </td>
                    </tr>
                  );
                  currentZone=zone;
                }

                if(entry.type==='item'){tbody.push(renderItemRow(entry.item));return;}

                var asmKey=entry.key;
                var asmItems=asmMap[asmKey];
                var firstItem=asmItems[0]||{};
                var asmObj=firstItem.assemblyId?(p.assemblies||[]).find(function(a){return String(a.id)===String(firstItem.assemblyId);}):null;
                var UUID_RE=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                var rawFallback=asmKey.replace(/^(id:|nm:)/,'');
                var asmDisplayName=asmObj?asmObj.name:(firstItem.assemblyName||(UUID_RE.test(rawFallback)?(firstItem.schedule?firstItem.schedule+' Assembly':'Assembly'):rawFallback));
                var asmSchedule=asmObj?asmObj.schedule:(firstItem.schedule||'');
                var compTypes=ASSEMBLY_COMPONENTS[asmSchedule]||[];
                var asmAllSelected=asmItems.length>0&&asmItems.every(function(it){return selectedItemIds.has(it.id);});
                var asmHandler=function(e){e.stopPropagation();if(asmAllSelected){asmItems.forEach(function(it){if(selectedItemIds.has(it.id))toggleItemSelected(it.id);});}else{selectAllVisible(asmItems.map(function(it){return it.id;}));}};

                tbody.push(
                  <tr key={"asm-"+asmKey} style={{background:'#F7F7F5',borderBottom:'0.5px solid #D8D7D2'}}>
                    <td style={{padding:'0 8px',width:36,verticalAlign:'middle',textAlign:'center'}}>
                      {selectionMode&&(
                        <div onClick={asmHandler}
                          style={{width:15,height:15,borderRadius:3,
                            border:asmAllSelected?'1.5px solid #111111':'1.5px solid #D8D7D2',
                            background:asmAllSelected?'#111111':'#FFFFFF',
                            cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto'}}>
                          {asmAllSelected&&<svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </div>
                      )}
                    </td>
                    <td style={{width:110}}></td>
                    <td style={{padding:'4px 8px',width:44}}></td>
                    <td colSpan={7} style={{padding:'6px 10px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                        <span style={{fontSize:9,fontWeight:500,color:'#888880',letterSpacing:'0.12em',textTransform:'uppercase',border:'0.5px solid #D8D7D2',borderRadius:3,padding:'1px 5px'}}>Assembly</span>
                        <span style={{fontSize:12,fontWeight:500,color:'#111111'}}>{asmDisplayName}</span>
                        <span style={{fontSize:10,color:'#888880'}}>· {asmItems.length} item{asmItems.length!==1?'s':''}</span>
                        {asmObj&&(
                          <div style={{display:'flex',gap:4,marginLeft:4}}>
                            <button onClick={function(e){e.stopPropagation();setEditingAsm(asmObj);}} style={{height:22,padding:'0 8px',borderRadius:4,border:'0.5px solid #D8D7D2',background:'#FFFFFF',fontSize:10,fontWeight:500,color:'#555552',cursor:'pointer',fontFamily:'inherit'}}>Edit</button>
                            <button onClick={function(e){e.stopPropagation();setDeletingAsm({asm:asmObj,members:asmItems});}} style={{height:22,padding:'0 8px',borderRadius:4,border:'0.5px solid #F5C0B8',background:'#FFFFFF',fontSize:10,fontWeight:500,color:'#C8322A',cursor:'pointer',fontFamily:'inherit'}}>Delete</button>
                            {compTypes.length>0&&(
                              <button onClick={function(e){e.stopPropagation();openNewItemForAssembly(asmObj,'');}} style={{height:22,padding:'0 8px',borderRadius:4,border:'0.5px solid #D4007A',background:'#FFFFFF',fontSize:10,fontWeight:500,color:'#D4007A',cursor:'pointer',fontFamily:'inherit'}}>+ Add component</button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );

                asmItems.forEach(function(it,idx){
                  var isLast=idx===asmItems.length-1;
                  var rowSelected=selectionMode?selectedItemIds.has(it.id):false;
                  var rowBg=rowSelected?'#FFF9E6':'#FFFFFF';
                  var imgSrc=(it.images&&it.images.length>0)?it.images[it.heroIndex||0]&&it.images[it.heroIndex||0].url:(it.imageUrl||'');
                  tbody.push(
                    <tr key={'asmrow-'+it.id}
                      style={{borderBottom:'0.5px solid #EFEFEC',background:rowBg}}
                      onClick={function(){if(selectionMode)toggleItemSelected(it.id);}}>
                      <td style={{padding:0,width:36,position:'relative',verticalAlign:'middle'}}>
                        <div style={{position:'absolute',left:17,top:0,bottom:isLast?'50%':0,width:'0.5px',background:'#D8D7D2'}} />
                        <div style={{position:'absolute',left:17,top:'50%',width:10,height:'0.5px',background:'#D8D7D2'}} />
                        <div style={{display:'flex',justifyContent:'center',alignItems:'center',padding:'8px 0',position:'relative',zIndex:1}}>
                          {selectionMode&&(
                            <div onClick={function(e){e.stopPropagation();toggleItemSelected(it.id);}}
                              style={{width:14,height:14,borderRadius:3,
                                border:rowSelected?'1.5px solid #111111':'1.5px solid #D8D7D2',
                                background:rowSelected?'#111111':'#FFFFFF',
                                cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                              {rowSelected&&<svg width="8" height="6" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{padding:'8px 10px',width:110,verticalAlign:'middle'}}>
                        <span style={{fontSize:9,color:it.itemNumber?'#555552':'#D8D7D2',fontWeight:400,whiteSpace:'nowrap'}}>
                          {it.itemNumber||'—'}
                        </span>
                      </td>
                      <td style={{padding:'6px 8px',width:44,verticalAlign:'middle'}}>
                        {imgSrc
                          ?<img src={imgSrc} alt="" onError={function(e){e.target.style.display='none';}} style={{width:36,height:36,objectFit:'cover',borderRadius:4,border:'0.5px solid #D8D7D2',display:'block'}} />
                          :<div style={{width:36,height:36,borderRadius:4,border:'0.5px dashed #D8D7D2',background:'#F7F7F5'}} />
                        }
                      </td>
                      <td style={{padding:'8px 10px',cursor:selectionMode?'default':'pointer'}}
                        onClick={function(){if(!selectionMode){setEditing(it);setMode('edit');window._hasUnsavedChanges=true;if(p.onNavigate)p.onNavigate('/items/'+it.id);}}}>
                        <div style={{display:'flex',alignItems:'center',gap:5}}>
                          <StatusDot status={it.status} />
                          {it.childType&&<span style={{fontSize:9,color:'#888880',fontWeight:500,marginRight:2}}>{it.childType}</span>}
                          <span style={{fontSize:12,fontWeight:500,color:taxNameColor(it),overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:280}}>
                            {it.productName||it.item||''}
                          </span>
                          {isABD(it)&&<span style={{fontSize:9,fontWeight:500,padding:'1px 5px',borderRadius:3,border:'0.5px solid #E0E0DC',background:'#fff',color:'#888880',marginLeft:2,whiteSpace:'nowrap'}}>ABD</span>}
                        {it.procurementClass==='owner_furnished'&&<span data-testid="procurement-class-badge" data-class="owner_furnished" title="Owner Furnished — client supplies the item; excluded from billing" style={{fontSize:9,fontWeight:500,padding:'1px 5px',borderRadius:3,border:'0.5px solid #E0E0DC',background:'#fff',color:'#888880',marginLeft:2,whiteSpace:'nowrap'}}>OF</span>}
                        {it.procurementClass==='by_others'&&<span data-testid="procurement-class-badge" data-class="by_others" title="By Others — installed by another trade; excluded from billing" style={{fontSize:9,fontWeight:500,padding:'1px 5px',borderRadius:3,border:'0.5px solid #E0E0DC',background:'#fff',color:'#888880',marginLeft:2,whiteSpace:'nowrap'}}>BO</span>}
                        </div>
                      </td>
                      <td style={{padding:'8px 10px',fontSize:11,color:'#555552',width:120}}>{it.project||'—'}</td>
                      <td style={{padding:'8px 10px',fontSize:11,color:'#555552',width:100}}>{it.roomName||'—'}</td>
                      <td style={{padding:'8px 10px',fontSize:11,color:'#555552',width:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{it.vendor||'—'}</td>
                      <td style={{padding:'8px 10px',width:100}}>
                        {it.netPrice?<div style={{fontSize:11,fontWeight:500,color:'#111111'}}>{fmtInput(it.netPrice)}</div>:<div style={{fontSize:11,color:'#D8D7D2'}}>—</div>}
                        <div style={{fontSize:10,color:'#888880',marginTop:1}}>{(it.qty||'')+(it.qtyType?' '+it.qtyType:'')}</div>
                      </td>
                      <td style={{padding:'8px 10px',fontSize:11,color:'#555552',width:100}}>{it.status||'—'}</td>
                    {showTaxCol&&<td style={{padding:'8px 10px',fontSize:11,whiteSpace:'nowrap'}} data-testid={'tax-remitted-'+it.id}>{taxRemittedCell(it)}</td>}
                    </tr>
                  );
                });
              });

              var allGroupChecked=groupItems.length>0&&groupItems.every(function(it){return selectedItemIds.has(it.id);});
              return (
                <div key={sched} style={{marginBottom:16,border:"1px solid #e0e0e0",borderRadius:8,overflow:"hidden"}}>
                  <div style={{background:"#f5f5f5",padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:isExp?"1px solid #e0e0e0":"none"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      {selectionMode && ((allGroupChecked&&groupItems.length>0)
                        ? <div onClick={function(){
                              groupItems.forEach(function(it){if(selectedItemIds.has(it.id))toggleItemSelected(it.id);});
                            }}
                            style={{width:15,height:15,borderRadius:3,flexShrink:0,
                              border:'1.5px solid #111111',background:'#111111',
                              cursor:'pointer',display:'flex',alignItems:'center',
                              justifyContent:'center'}}>
                            <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                              <path d="M1 3.5L3.5 6L8 1" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        : <div onClick={function(){
                              selectAllVisible(groupItems.map(function(it){return it.id;}));
                            }}
                            style={{width:15,height:15,borderRadius:3,flexShrink:0,
                              border:'1.5px solid #D8D7D2',background:'#FFFFFF',
                              cursor:'pointer',display:'flex',alignItems:'center',
                              justifyContent:'center'}}>
                          </div>
                      )}
                      <div onClick={function(){toggleGroup(sched);}} style={{fontWeight:700,fontSize:13,cursor:"pointer"}}>
                        {groupLabel} <span style={{fontSize:11,color:"#999",fontWeight:400}}>{"("+groupItems.length+" item"+(groupItems.length!==1?"s":"")+")"}</span>
                      </div>
                    </div>
                    <span onClick={function(){toggleGroup(sched);}} style={{fontSize:12,color:"#999",cursor:"pointer"}}>{isExp?"▾":"▸"}</span>
                  </div>
                  {isExp&&(
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                      <thead>
                        <tr style={{background:"#fff"}}>
                          {["","Item #","","Item","Project","Room","Vendor","Qty / Net","Status"].concat(showTaxCol?["Remitted"]:[]).map(function(hd,hi){
                            return <th key={hi} style={{padding:"8px 10px",textAlign:"left",fontWeight:500,fontSize:10,letterSpacing:"0.08em",textTransform:"uppercase",color:"#bbb",borderBottom:"1px solid #E0E0DC"}}>{hd}</th>;
                          })}
                        </tr>
                      </thead>
                      <tbody>{tbody}</tbody>
                    </table>
                  )}
                </div>
              );
            })}
            {summaryBar}
          </div>
      }
    </div>
  );
}
